import { query, stagger, style, transition, trigger, useAnimation } from '@angular/animations';
import { Component, ElementRef, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fadeInDown } from 'ng-animate';
import { IFundraiser } from '../../../../fundraiser/models/fundraiser.model';
import { IUTM } from '../../../../shared/models';
import { EventsService, FbService, ShareMessagesService, UtilService, VariablesService, VwoService } from '../../../../shared/services';
import { FacebookService, UIParams } from 'ngx-facebook';

@Component({
  selector: 'app-social-share',
  templateUrl: './social-share.component.html',
  styleUrls: ['./social-share.component.scss'],
  animations: [
    trigger('listAnimation', [transition('* => *', [
      query(':enter', style({ opacity: 0 }), { optional: true }),
      query(':enter', [stagger(100, [useAnimation(fadeInDown)])])
    ])]),
  ]
})
export class SocialShareComponent implements OnInit {

  private readonly vwo = inject(VwoService);
  private readonly events = inject(EventsService);

  @Input() fundraiser: IFundraiser;
  @Input() metaData;
  @Input() sharedCounts;
  @Input() basicInfo: any;
  @Input() isFundraiserEnded;
  @Input() shareType = 'overlay';
  @Input() both;
  @Input() label = 'Share via Whatsapp';
  shareButtons = [
    {
      color: '#13cb6a', button: 'whatsapp', icon: 'icon-whatsapp i-whatsapp', label: 'Whatsapp', medium: 'whatsapp_share',
      device: 'desktop', pos: ''
    },
    {
      color: '#E1306C', button: 'instagram', icon: 'share-btn icon-instagram1 i-instagram', label: 'Instagram', medium: 'insta_share', pos: 1
    },
    {
      color: '#3b5998', button: 'facebook', icon: 'share-btn icon-facebook i-fb btn-fb', label: 'Facebook',
      medium: 'fbShare', pos: ''
    },
    { color: '#2c99d7', button: 'twitter', icon: 'share-btn icon-twitter i-tweet btn-twitter', label: 'Tweet', medium: 'twShare', pos: 1 },
    {
      color: '#0077B5', button: 'linkedin', icon: 'share-btn icon-linkedin i-linkedin btn-linkedin', label: 'LinkedIn',
      medium: 'linkedin', pos: 1
    },
    { color: '#ea4335', button: 'email', icon: 'share-btn icon-google i-gp btn-gmail', label: 'Mail', medium: 'email', pos: 1 },
    { color: '#01bfbd', button: 'copy', icon: 'share-btn icon-chain i-link', label: 'Link', medium: '', pos: 1 },
  ];
  isShareMenu = false;
  @ViewChild('socailShare', { static: true }) public socailShare: ElementRef<HTMLElement>;

  constructor(
    public utilService: UtilService,
    public variablesService: VariablesService,
    public shareMessagesService: ShareMessagesService,
    @Inject(DOCUMENT) private document: any,
    private fbService: FbService,
    public fb: FacebookService,
  ) { }

  ngOnInit(): void {
    this.shareButtons.map((data, index) => {
      if (data?.button?.match('whatsapp|facebook')) {
        this.shareButtons[index]['pos'] = this.utilService.isMobile() ? 'PosOS1' : 'PosOS';
      }
    });
    if (!this.vwo.insta_share_AB()) {
      this.shareButtons = this.shareButtons.filter(item => item.button !== 'instagram');
    }
    this.fundraiser = this.variablesService.fundraiser;
    this.basicInfo = this.variablesService.basicInfo;
  }

  /**Get the url of for share */
  geturl(btn) {
    if (this.utilService.isBrowser && this.fundraiser) {
      const data: IUTM = {
        source: 'internal',
        medium: btn.medium,
        campaign_name: this.fundraiser.custom_tag,
        term: btn?.term || ''
      };
      const page = this.variablesService.campaignType === 'stories' ? 'stories' : 'fundraiser';
      const link = `${this.utilService.domain_details.domain_url}/${page}/${this.fundraiser.custom_tag}`;
      return this.shareMessagesService.getLinkWithUtmTags(link, data);
    } else {
      return '';
    }
  }
  /**Social share */
  socialShare() {
    if ((<any>navigator).share) {
      (<any>navigator).share({
        title: this.metaData?.title,
        text: this.metaData?.description?.trim(),
        url: `${this.geturl({ medium: 'nativeShare', term: this.variablesService.pageName })}`
      }).then(() => { }).catch((error) => { });
      this.events.sendSystemEvent({ eventName: 'nativeShare' }).subscribe(_ => _);
    } else {
      this.socailShare.nativeElement.scrollIntoView(
        { behavior: 'smooth', block: 'start', inline: 'start' }
      );
      this.isShareMenu = !this.isShareMenu;
      this.document.body.style.overflow = this.isShareMenu ? 'hidden' : 'auto';
    }
  }
  shareEvent(btn) {
    this.shareMessagesService.shareEvents(btn.medium, btn.pos);
    switch (btn.button) {
      case 'whatsapp': this.shareOnWhatsApp(); break;
      case 'facebook': this.shareOnFb(this.geturl(btn)); break;
      case 'twitter': this.shareOnTwitter(this.geturl(btn)); break;
      case 'linkedin': this.shareOnLinked(this.geturl(btn)); break;
      case 'email': this.shareOnGmail(); break;
      case 'instagram': this.shareOnInstagram(btn); break;
      case 'copy': this.copyURL(this.geturl(btn)); break;
    }
  }

  shareOnTwitter(url) {
    const size = 'left=0,top=0,width=550,height=450,personalbar=0,toolbar=0,scrollbars=0,resizable=0';
    const text = encodeURIComponent(this.fundraiser.title);
    window.open(`http://twitter.com/intent/tweet?url=${encodeURIComponent(url)}` + `&text=${text}`, '', size);
  }

  async shareOnWhatsApp() {
    const message = await this.shareMessagesService.getWhatsAppMessage(this.fundraiser, this.basicInfo, this.isFundraiserEnded);
    const whatsAppDomain = this.utilService.isMobile() ? 'api' : 'web';
    this.utilService.nativeNavigation(`https://${whatsAppDomain}.whatsapp.com/send?text=${message}`, true);
  }

  async shareOnFb(url) {
    const fbLoad = this.fbService.scriptLoaderFB();
    if (fbLoad) {
      const params: UIParams = {
        href: `${url}`,
        method: 'share',
        message: this.fundraiser.title || ''
      };
      this.fb.ui(params);
    }
  }

  shareOnLinked(url) {
    window.open(`http://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`, 'sharer', 'toolbar=0, status=0, width=626, height=436');
  }

  async shareOnGmail() {
    const message = await this.shareMessagesService.getWhatsAppMessage(this.fundraiser, this.basicInfo, this.isFundraiserEnded, { utm_medium: 'emailShare', utm_term: 'emailShare' }, true);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=&su=Your+Subject+here&body=${message}&ui=2&tf=1&pli=1`, 'sharer', 'toolbar=0,status=0,width=648,height=395');
  }



  copyURL(url) {
    const copyText = url;
    const textArea = this.document.createElement('textarea');
    textArea.value = copyText;
    this.document.body.appendChild(textArea);
    textArea.select();
    this.document.execCommand('Copy');
    textArea.remove();
  }

  shareOnInstagram(data) {
    this.shareMessagesService.instagramShare(data, this.fundraiser);
  }

}
